import type { ApiEnvelope } from '@/types/api'
import { verificarVersaoPublicada } from '@/utility/versao-publicada'
import { ApiError, ForbiddenError, NetworkError, UnauthorizedError } from './errors'

const BASE_URL = import.meta.env.VITE_API_BACKOFFICE_URL
const APP_KEY = import.meta.env.VITE_API_APP_KEY

let getToken: () => string | null = () => null
let handleUnauthorized: () => void = () => {}

export const configureApi = (options: {
  getToken: () => string | null
  onUnauthorized: () => void
}) => {
  getToken = options.getToken
  handleUnauthorized = options.onUnauthorized
}

type QueryPrimitive = string | number | boolean | Date

export type QueryValue = QueryPrimitive | QueryPrimitive[] | null | undefined

const toQueryParam = (value: QueryPrimitive): string =>
  value instanceof Date ? value.toISOString() : String(value)

const buildQuery = (query: Record<string, QueryValue> | undefined): string => {
  if (!query) return ''
  const params = new URLSearchParams()

  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue

    if (Array.isArray(value)) {
      for (const item of value) {
        if (item === undefined || item === null || item === '') continue
        params.append(key, toQueryParam(item))
      }
      continue
    }

    params.append(key, toQueryParam(value))
  }

  const queryString = params.toString()
  return queryString ? `?${queryString}` : ''
}

interface FetchAutenticadoOptions {
  method?: 'DELETE' | 'GET' | 'POST' | 'PUT'
  query?: Record<string, QueryValue>
  auth?: boolean
  signal?: AbortSignal
  headers?: HeadersInit
  body?: BodyInit
}

const fetchAutenticado = async (
  path: string,
  init: FetchAutenticadoOptions = {},
): Promise<Response> => {
  const { auth = true, body, headers: extraHeaders, method = 'GET', query, signal } = init

  if (!BASE_URL) {
    throw new ApiError('URL base da API não configurada, confira o .env.', 0)
  }

  const headers = new Headers(extraHeaders)

  if (auth) {
    const token = getToken()
    if (token) headers.set('Authorization', `Bearer ${token}`)
  } else if (APP_KEY) {
    headers.set('X-App-Key', APP_KEY)
  }

  let response: Response
  try {
    response = await fetch(`${BASE_URL}${path}${buildQuery(query)}`, {
      method,
      headers,
      body,
      signal,
    })
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    throw new NetworkError()
  }

  void verificarVersaoPublicada()

  if (response.status === 401) {
    handleUnauthorized()
    throw new UnauthorizedError()
  }

  return response
}

interface RequestOptions {
  method?: 'DELETE' | 'GET' | 'POST' | 'PUT'
  path: string
  body?: unknown
  query?: Record<string, QueryValue>
  auth?: boolean
  signal?: AbortSignal
}

const parseBody = async (response: Response): Promise<unknown> => {
  if (response.status === 204) return null

  const text = await response.text()
  if (!text) return null

  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

const isEnvelope = (value: unknown): value is ApiEnvelope<unknown> =>
  typeof value === 'object' && value !== null && 'sucesso' in value

const extractErros = (payload: unknown): string[] => {
  if (isEnvelope(payload) && Array.isArray(payload.erros)) return payload.erros

  if (typeof payload === 'object' && payload !== null) {
    const { mensagens } = payload as { mensagens?: unknown }
    if (Array.isArray(mensagens)) return mensagens.map(String)
    if (typeof mensagens === 'string') return [mensagens]
  }

  return []
}

export async function request<T>(options: RequestOptions): Promise<T | null> {
  const { auth = true, body, method = 'GET', path, query, signal } = options

  const headers = new Headers({ Accept: 'application/json' })
  if (body !== undefined) headers.set('Content-Type', 'application/json')

  const response = await fetchAutenticado(path, {
    method,
    query,
    auth,
    signal,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  })

  const payload = await parseBody(response)
  const erros = extractErros(payload)

  if (response.status === 403) throw new ForbiddenError(erros[0])

  if (!response.ok) {
    throw new ApiError(
      erros[0] ?? `A requisição falhou (HTTP ${response.status}).`,
      response.status,
      erros,
    )
  }

  if (payload === null) return null

  if (isEnvelope(payload)) {
    if (!payload.sucesso) {
      throw new ApiError(erros[0] ?? 'A operação não foi concluída.', response.status, erros)
    }
    return (payload.data ?? null) as T | null
  }

  return payload as T
}

interface DownloadOptions {
  path: string
  query?: Record<string, QueryValue>
  nomeArquivoPadrao: string
}

const extrairNomeArquivo = (disposition: string | null): string | null => {
  if (!disposition) return null
  const match = /filename="?([^";]+)"?/.exec(disposition)
  return match ? match[1] : null
}

export async function downloadArquivo(options: DownloadOptions): Promise<void> {
  const { nomeArquivoPadrao, path, query } = options

  const response = await fetchAutenticado(path, { query })

  if (response.status === 403) throw new ForbiddenError()

  if (!response.ok) {
    throw new ApiError(
      `Não foi possível baixar o arquivo (HTTP ${response.status}).`,
      response.status,
    )
  }

  const nomeArquivo =
    extrairNomeArquivo(response.headers.get('Content-Disposition')) ?? nomeArquivoPadrao

  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = nomeArquivo
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}
